import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { propertyId, checkIn, checkOut, numGuests, pricing, discountCode, discountAmount } = location.state || {};

  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  if (!propertyId || !checkIn || !checkOut) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-primary mb-4">Invalid Booking</h2>
          <Button onClick={() => navigate('/')}>Return to Home</Button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, {
        property_id: propertyId,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        check_in: checkIn,
        check_out: checkOut,
        num_guests: numGuests,
        total_price: pricing.total_price,
        discount_code: discountCode || null,
        discount_amount: discountAmount || null
      });

      const newBookingId = bookingResponse.data.id;
      setBookingId(newBookingId);

      // Mark booking as confirmed (payments handled by Guesty)
      await axios.patch(`${API}/bookings/${newBookingId}?payment_status=paid&status=confirmed`);

      setBookingComplete(true);
      toast.success("Booking confirmed!");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to complete booking");
    } finally {
      setLoading(false);
    }
  };

  if (bookingComplete && bookingId) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto px-4 text-center"
          >
            <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-4xl font-heading font-bold text-primary mb-4" data-testid="booking-success-title">
              Booking Confirmed!
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Thank you for your booking. Check your email for confirmation details.
            </p>
            <div className="bg-secondary rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-primary mb-4">Booking Details</h3>
              <div className="space-y-2">
                <p className="text-slate-700"><strong>Check-in:</strong> {checkIn}</p>
                <p className="text-slate-700"><strong>Check-out:</strong> {checkOut}</p>
                <p className="text-slate-700"><strong>Guests:</strong> {numGuests}</p>
                <p className="text-slate-700"><strong>Total:</strong> £{pricing.total_price}</p>
              </div>
            </div>
            <Button 
              size="lg"
              className="bg-accent hover:bg-accent/90 text-white rounded-full mr-4"
              onClick={() => navigate(`/guest-portal/${bookingId}`)}
              data-testid="access-portal-button"
            >
              Access Guest Portal
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            className="mb-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h1 className="text-3xl font-heading font-bold text-primary mb-6">
                Complete Your Booking
              </h1>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                <div>
                  <Label htmlFor="guest_name">Full Name</Label>
                  <Input
                    id="guest_name"
                    name="guest_name"
                    type="text"
                    required
                    value={formData.guest_name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    data-testid="guest-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="guest_email">Email</Label>
                  <Input
                    id="guest_email"
                    name="guest_email"
                    type="email"
                    required
                    value={formData.guest_email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    data-testid="guest-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="guest_phone">Phone Number</Label>
                  <Input
                    id="guest_phone"
                    name="guest_phone"
                    type="tel"
                    required
                    value={formData.guest_phone}
                    onChange={handleInputChange}
                    placeholder="+44 7XXX XXXXXX"
                    data-testid="guest-phone-input"
                  />
                </div>

                <div className="bg-secondary rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-2">
                    By clicking "Confirm & Pay", you agree to our terms and conditions.
                  </p>
                  <p className="text-xs text-slate-500">
                    Payment is processed securely via Stripe.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-accent hover:bg-accent/90 text-white py-6 text-lg rounded-full"
                  disabled={loading}
                  data-testid="confirm-payment-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm & Pay'
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Booking Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:sticky lg:top-32 h-fit"
            >
              <div className="bg-secondary rounded-lg p-6">
                <h2 className="text-2xl font-heading font-semibold text-primary mb-6">
                  Booking Summary
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Check-in</span>
                    <span className="font-medium text-primary">{checkIn}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Check-out</span>
                    <span className="font-medium text-primary">{checkOut}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Guests</span>
                    <span className="font-medium text-primary">{numGuests}</span>
                  </div>
                </div>

                {pricing && (
                  <>
                    <div className="border-t border-border pt-4 space-y-2 mb-4">
                      <div className="flex justify-between">
                        <span className="text-slate-600">£{pricing.price_per_night} x {pricing.nights} nights</span>
                        <span className="font-medium">£{pricing.total_price}</span>
                      </div>
                    </div>

                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-semibold text-primary">Total</span>
                        <span className="text-2xl font-bold text-primary">£{pricing.total_price}</span>
                      </div>
                    </div>

                    {pricing.savings > 0 && (
                      <div className="bg-accent/10 border border-accent/20 rounded p-3">
                        <p className="text-accent font-medium text-sm text-center">
                          You're saving £{pricing.savings} vs Airbnb!
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;